import React, { useEffect, useRef, useState } from 'react';
import { Button, Input, InputRef } from 'antd';
import { useStore } from '@/stores';
import { observer } from 'mobx-react-lite';
import { LeftSquareOutlined } from '@ant-design/icons';
import { State } from '@/services/state.service';
import { Dynamics } from '@/services/dynamics/dynamics';
import { IEntity } from '@/shared/interfaces/entity.interface';
import {
  ESolgariCallType,
  EStatusCode,
} from '@/services/dynamics/interfaces/api/phone-call.interface';
import { SipJs } from '@/services/sip-js.service';
import { commonMakeCall } from '@/shared/helpers/common-make-call.helper';
import './ExternalTransfer.scss';

const MAX_LENGTH = 20;
const ALLOW_SECOND_CALL = true;

export const ExternalTransfer = observer(() => {
  const {
    callStore: { setContacts },
  } = useStore();
  const [phoneNumber, setPhoneNumber] = useState('');
  const [inputCursorPosition, setInputCursorPosition] = useState({
    start: 0,
    end: 0,
  });

  const inputEl = useRef<InputRef>(null);

  useEffect(() => {
    const input = inputEl.current.input;
    const { start, end } = inputCursorPosition;

    input.setSelectionRange(start, end);
    input.focus();
  }, [inputCursorPosition]);

  const removeKey = () => {
    const start = inputEl.current.input.selectionStart;
    const end = inputEl.current.input.selectionEnd;

    if (start === 0 && end === 0) {
      setInputCursorPosition({ start, end });
      return;
    }

    if (start === end) {
      const newValue = phoneNumber.slice(0, start - 1) + phoneNumber.slice(end);
      setPhoneNumber(newValue);
      setInputCursorPosition({ start: start - 1, end: end - 1 });
      return;
    }

    const newValue = phoneNumber.slice(0, start) + phoneNumber.slice(end);
    setPhoneNumber(newValue);
    setInputCursorPosition({ start, end: start });
  };

  const blindTransfer = () => {
    const session = SipJs.sessions[0];
    SipJs.blindTransfer(session.id, phoneNumber);

    const { isDialerActive } = State.state$.getValue();

    Dynamics.savePhoneCallNote(
      {
        desc: '',
        entity: session.remoteUser.connectedUsers[0] as IEntity,
        timer: session.timer,
        extendedSubject: `, transferred to phone number ${phoneNumber}`,
        callState: EStatusCode.OPEN,
        callType: isDialerActive
          ? ESolgariCallType.DIALER
          : ESolgariCallType.REGULAR,
      },
      session,
    );
  };

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const phone = e.target.value;
    const sipPhone = phone.replace(/[^0-9*#]/g, '');
    setPhoneNumber(sipPhone);
  };

  const makeExternalCall = async () => {
    commonMakeCall([], phoneNumber, ALLOW_SECOND_CALL);
    const entities = await Dynamics.searchEntityByCalls({
      phone: phoneNumber,
      isExactPhoneSearch: true,
    });
    if (entities?.length) {
      setContacts(entities);
    }
  };

  const removeBtnRender = (
    <Button
      className="btn-after"
      onClick={removeKey}
      icon={<LeftSquareOutlined />}
    ></Button>
  );

  return (
    <div className="external-transfer">
      <div className="external-transfer">
        <Input
          data-testid="external-transfer-input"
          addonBefore="+"
          autoFocus
          ref={inputEl}
          value={phoneNumber}
          placeholder={'Phone number'}
          addonAfter={removeBtnRender}
          maxLength={MAX_LENGTH}
          onChange={onChange}
        />
        {phoneNumber?.length === MAX_LENGTH && (
          <div className="external-transfer__input-warning">
            The phone number is too long, please check it
          </div>
        )}
      </div>
      <div className="external-transfer__controls">
        <Button
          disabled={!phoneNumber}
          data-testid="call-first"
          className="agent-list__btn answer--black"
          onClick={() => makeExternalCall()}
        >
          Call First
        </Button>
        <Button
          disabled={!phoneNumber}
          className="external-transfer__btn"
          data-testid="blind-transfer"
          type="primary"
          onClick={() => blindTransfer()}
        >
          Blind Transfer
        </Button>
      </div>
    </div>
  );
});
